/* eslint-disable react-refresh/only-export-components */
import { SvgIcon, SvgIconProps } from '@mui/material';
import {
  Home,
  Bell,
  BellOutline,
  HelpCircleOutline,
  ChartTree,
  ProductWorkflowBuilder as WorkflowBuilder,
  Cog,
  ViewDashboard,
  ViewSplitVertical,
  ProductReportingTransparency,
  ProductAppBuilder,
  ProductAssetManagement,
  ProductBp,
  ProductOpenData,
  Calendar,
  ProductFinance,
  CashMultiple,
  CreditCard,
  ProductPlc,
  ProductClm,
  ProductProcurement,
  ProductUtilityBilling,
  Wrench,
  AccountCog,
  ChevronRight,
  ChevronDown,
  MenuDown,
  ArrowULeftTop,
  OpenInNew,
  CommentMultipleOutline,
  CashPlus,
  FileTree,
  BookOpenOutline,
  ClipboardListOutline,
  ApplicationCog,
  ScaleBalance,
  Xml,
  DatabaseOutline,
  BullseyeArrow,
  AccountGroup,
  AccountHardHat,
  ForumOutline,
  Bookmark,
  BookmarkOutline,
  ClipboardTextClockOutline,
  Close,
  Plus,
  Pencil,
  DragVertical,
  Star,
  StarOutline,
  LinkVariant,
  Pin,
  PushPinOutlined,
  Magnify,
  Product311,
  HandCoin,
  ViewGrid,
  Density1,
} from '@opengov/react-capital-assets';
import { MenuGroupName, EntityApplicationProductKey } from '../types';

/**
 * Creates a reusable icon component from a Capital Assets icon
 * Wraps the icon in MUI's SvgIcon with consistent styling
 * @param IconComponent - The Capital Assets icon component to wrap
 * @returns A React component that accepts SvgIconProps (aria-label should be provided when using the icon)
 */
export const createIcon = (IconComponent: React.ElementType): React.ComponentType<SvgIconProps> => {
  return (props: SvgIconProps) => {
    const { sx, ...rest } = props;
    return (
      <SvgIcon
        component={IconComponent}
        inheritViewBox
        sx={{ color: 'inherit', m: 0, ...sx }}
        {...rest}
      />
    );
  };
};

// Exported icon components for direct use (e.g., in SectionHeader)
// These don't need aria-labels as they're used as decorative elements in specific contexts
export const ExpandIcon = createIcon(ChevronRight);
export const CollapseIcon = createIcon(ChevronDown);
// Pin (filled) / PinOutline (outline) — used by AllProductsPage to toggle a
// product's pinned state. Outline = not pinned, filled = pinned.
export const PinIcon = createIcon(Pin);
export const PinOutlineIcon = createIcon(PushPinOutlined);
// Search/magnifier — used by AllProductsPage's search field and empty state.
export const SearchIcon = createIcon(Magnify);
// View mode toggle icons — used by ProductsToolbar to switch between grid and table layouts.
export const ViewGridIcon = createIcon(ViewGrid);
export const Density1Icon = createIcon(Density1);

/**
 * Raw icon mapping - Capital Assets component to use for each icon name
 * This is transformed below to add aria-labels dynamically
 */
export const rawIconMap = {
  NewTab: createIcon(OpenInNew),
  ChevronRight: createIcon(ChevronRight),
  ExpandMore: createIcon(ChevronDown),
  MenuDown: createIcon(MenuDown),
  Home: createIcon(Home),
  Notifications: createIcon(Bell),
  NotificationsOutline: createIcon(BellOutline),
  HelpCircleOutline: createIcon(HelpCircleOutline),
  AccountTree: createIcon(ChartTree),
  Settings: createIcon(Cog),
  Dashboard: createIcon(ViewDashboard),
  ViewSplitVertical: createIcon(ViewSplitVertical),
  Assessment: createIcon(ProductReportingTransparency),
  Apps: createIcon(ProductAppBuilder),
  Build: createIcon(ProductAssetManagement),
  Timeline: createIcon(ProductBp),
  BarChart: createIcon(ProductFinance),
  CardGiftcard: createIcon(CashMultiple),
  Description: createIcon(ProductPlc),
  ShoppingCart: createIcon(ProductProcurement),
  Receipt: createIcon(ProductUtilityBilling),
  Person: createIcon(Cog),
  AdminPanelSettings: createIcon(AccountCog),
  SwapHoriz: createIcon(ArrowULeftTop),
  Xml: createIcon(Xml),
  // App registry icon mappings
  AgentStudio: FileTree,
  AppBuilder: Wrench,
  AssetManagement: ProductAssetManagement,
  BankReconciliation: CreditCard,
  BudgetingPerformance: Calendar,
  CommunityFeedback: CommentMultipleOutline,
  Financials: ProductFinance,
  GeneralLedger: BookOpenOutline,
  GrantsManagement: HandCoin,
  Hcm: AccountGroup,
  Payroll: CashMultiple,
  PermittingLicensing: ClipboardListOutline,
  Procurement: ProductProcurement,
  VendorManagement: AccountGroup,
  WorkforcePlanning: AccountHardHat,
  ContractManagement: ProductClm,
  Reporting: ProductReportingTransparency,
  TaxRevenue: CashPlus,
  UtilityBilling: ProductUtilityBilling,
  CommandCenter: Home,
  AdminSettings: AccountCog,
  AuditLog: ClipboardTextClockOutline,
  DataManager: DatabaseOutline,
  PerformanceManagement: BullseyeArrow,
  Product311: Product311,
  OpenData: ProductOpenData,
  WorkflowBuilder: WorkflowBuilder,
  // Dev tools
  AppRegistryEditor: ApplicationCog,
  Wrench: Wrench,
  // Test apps
  FinanceDashboard: createIcon(Xml),
  ErpGeneralLedger: createIcon(Xml),
  ErpGeneralLedgerNewTab: createIcon(Xml),
  AgentStudioLite: createIcon(Xml),
  Og360: createIcon(Xml),
  Legis: createIcon(ScaleBalance),
  ForumOutline: createIcon(ForumOutline),
  Bookmark: Bookmark,
  BookmarkOutline: createIcon(BookmarkOutline),
  Star: createIcon(Star),
  StarOutline: createIcon(StarOutline),
  Close: Close,
  // Favorites feature: add-to-favorites, inline rename, drag-to-reorder
  Add: createIcon(Plus),
  Edit: createIcon(Pencil),
  DragHandle: createIcon(DragVertical),
  Link: createIcon(LinkVariant),
  Pin: Pin,
  PinOutline: PushPinOutlined,
  Search: Magnify,
} as const;

/**
 * Icon mapping object
 * Maps string icon names (from navigationConfig) to React components
 * Users should provide aria-label prop when using these icons
 */
export const iconMap = (Object.keys(rawIconMap) as Array<keyof typeof rawIconMap>).reduce(
  (iconComponents, key) => {
    iconComponents[key] = createIcon(rawIconMap[key]);
    return iconComponents;
  },
  {} as Record<keyof typeof rawIconMap, React.ComponentType<SvgIconProps>>
);

/**
 * Type representing all valid icon names
 * This type automatically updates when icons are added/removed from iconMap
 */
export type IconName = keyof typeof iconMap;

/**
 * Get icon component by name
 * Returns the corresponding icon component or a fallback
 * Users should provide aria-label prop when using the returned icon
 */
export const getIconComponent = (iconName: string): React.ComponentType<SvgIconProps> => {
  if (iconName in iconMap) {
    return iconMap[iconName as IconName];
  }
  return createIcon(Home); // Fallback to Home icon
};

/**
 * Maps menu group names to their corresponding icon names.
 * This mapping ensures type safety between MenuGroupName and IconName.
 */
const menuGroupIconMap: Record<MenuGroupName, IconName> = {
  [MenuGroupName.FINANCIALS]: 'Financials',
  [MenuGroupName.HUMAN_CAPITAL_MANAGEMENT]: 'Hcm',
} as const;

/**
 * Gets the icon name for a menu group.
 * Useful when you need the IconName string (e.g., for MenuItem.icon).
 * @param groupName - The MenuGroupName enum value
 * @returns The IconName for the specified menu group
 */
export const getMenuGroupIconName = (groupName: MenuGroupName): IconName => {
  return menuGroupIconMap[groupName];
};

/**
 * Maps EntityApplicationProductKey to their corresponding icon names.
 * Used for displaying product indicators in notifications and other features.
 */
const productAppIconMap: Partial<Record<EntityApplicationProductKey, IconName>> = {
  [EntityApplicationProductKey.PROCUREMENT]: 'Procurement',
  [EntityApplicationProductKey.FINANCIALS]: 'Financials',
  [EntityApplicationProductKey.UTILITY_BILLING]: 'UtilityBilling',
  [EntityApplicationProductKey.BUDGET]: 'BudgetingPerformance',
  [EntityApplicationProductKey.ASSET_MANAGEMENT]: 'AssetManagement',
  [EntityApplicationProductKey.TAX_REVENUE]: 'TaxRevenue',
  [EntityApplicationProductKey.PERMITTING_LICENSING]: 'PermittingLicensing',
  [EntityApplicationProductKey.COMMUNITY_FEEDBACK]: 'CommunityFeedback',
  [EntityApplicationProductKey.ADMIN_SETTINGS]: 'AdminSettings',
  [EntityApplicationProductKey.REPORTING]: 'Reporting',
  [EntityApplicationProductKey.AGENT_STUDIO]: 'AgentStudio',
  [EntityApplicationProductKey.APP_BUILDER]: 'AppBuilder',
  [EntityApplicationProductKey.VENDOR_MANAGEMENT]: 'VendorManagement',
  [EntityApplicationProductKey.CLM]: 'ContractManagement',
  [EntityApplicationProductKey.DEVELOPER_PORTAL]: 'Settings', // Fallback
  [EntityApplicationProductKey.OD]: 'Dashboard', // Fallback
  [EntityApplicationProductKey.DATA_MANAGER]: 'DataManager',
  [EntityApplicationProductKey.WORKFLOW_BUILDER]: 'WorkflowBuilder',
  [EntityApplicationProductKey.HCM]: 'Hcm',
  [EntityApplicationProductKey.PAYROLL]: 'Payroll',
  [EntityApplicationProductKey.WORKFORCE_PLANNING]: 'WorkforcePlanning',
  [EntityApplicationProductKey.LMS]: 'Hcm',
  [EntityApplicationProductKey.PRODUCT_311]: 'Product311',
  [EntityApplicationProductKey.GRANTS_MANAGEMENT]: 'GrantsManagement',
} as const;

/**
 * Gets the icon component for a product application.
 * Used for displaying product indicators in notifications and other features.
 * @param product - The EntityApplicationProductKey value
 * @returns The icon component for the specified product application, or Home icon as fallback
 */
export const getProductAppIconComponent = (
  product: EntityApplicationProductKey
): React.ComponentType<SvgIconProps> => {
  const iconName = productAppIconMap[product] ?? 'Home';
  return getIconComponent(iconName);
};
